import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Globe, MapPin, Calendar, ChevronRight } from 'lucide-react';
import { appendUtm } from '../lib/utm';
import { EditableText } from './inline/EditableText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [gc, setGc] = useState(null);
  const [visibility, setVisibility] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/content/global`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGc(d); })
      .catch(() => {});
    fetch(`${API_URL}/api/content/visibility`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setVisibility(d); })
      .catch(() => {});
  }, []);

  const isPageVisible = (pageKey) => {
    if (!visibility) return true;
    const p = visibility.pages?.[pageKey];
    return !(p && p.visible === false);
  };

  const eventDate = gc?.event_dates_display || 'Coming January 2027';
  const location = gc?.event_location || 'University of Tennessee, Knoxville';
  const contactEmail = gc?.general_inquiry_email || 'contact@usceef.org';
  const websiteUrl = gc?.site_url || 'https://www.usceef.org';
  const websiteLabel = gc?.site_url_label || 'www.usceef.org';
  const registerUrl = gc?.registration_url || 'https://utconferences.eventsair.com/cee/registration/Site/Register';
  const registerCta = gc?.registration_cta_text || 'Register Now';
  const footerCopyright = gc?.footer_copyright || '© 2027 World Nuclear Congress. All rights reserved.';
  const footerTagline = gc?.footer_tagline || 'Advancing nuclear energy for a sustainable future.';
  const siteName = gc?.event_name || 'World Nuclear Congress';
  const footerDescription = gc?.footer_description || 'The premier international forum for the nuclear industry, bringing together global leaders to advance nuclear energy.';

  const siteLinks = [
    { name: 'Home', path: '/', pageKey: 'home' },
    { name: 'Program', path: '/program', pageKey: 'program' },
    { name: 'Speakers', path: '/speakers', pageKey: 'speakers' },
    { name: 'Sponsorship', path: '/sponsorship', pageKey: 'sponsorship' },
    { name: 'Golf Outing', path: '/golf-outing', pageKey: 'golf' },
  ].filter(link => isPageVisible(link.pageKey));

  return (
    <footer className="bg-[#00457f] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Logo and Info */}
          <div className="space-y-4">
            <img
              src="/assets/images/wnc-logo.png"
              alt={siteName}
              className="h-20 w-auto"
            />
            <p className="text-gray-300 text-sm leading-relaxed">
              <EditableText as="span" scope={{ type: 'global', field: 'footer_description' }} value={footerDescription}>{footerDescription}</EditableText>
            </p>
          </div>

          {/* Site Map */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text">
              Site Map
            </h3>
            <nav className="space-y-2">
              {siteLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm group"
                >
                  <ChevronRight className="w-4 h-4 text-yellow-400 group-hover:translate-x-1 transition-transform" />
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text">
              Event Details
            </h3>
            <div className="space-y-3 text-gray-300">
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white" data-testid="footer-event-date">{eventDate}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white">Knoxville, Tennessee</p>
                  <p className="text-sm">United States of America</p>
                </div>
              </div>
              <p className="text-sm pl-7">
                Hosted by CEE at the {location}
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text">
              Contact Us
            </h3>
            <div className="space-y-3">
              <a
                href={`mailto:${contactEmail}`}
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <Mail className="w-5 h-5 text-yellow-400" />
                <span className="text-sm">{contactEmail}</span>
              </a>
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
              >
                <Globe className="w-5 h-5 text-yellow-400" />
                <span className="text-sm">{websiteLabel}</span>
              </a>
            </div>
            {/* Register CTA */}
            <a
              href={appendUtm(registerUrl, { utm_medium: 'footer' })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-yellow-500 to-amber-500 text-[#0a1a2f] px-6 py-2.5 rounded-full font-bold text-sm hover:from-yellow-400 hover:to-amber-400 transition-all transform hover:scale-105"
              data-testid="footer-register-cta"
            >
              {registerCta}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-600/50 bg-[#003366]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left">
            <EditableText as="span" scope={{ type: 'global', field: 'footer_copyright' }} value={footerCopyright} multiline={false}>{footerCopyright}</EditableText>
          </p>
          <p className="text-gray-500 text-xs text-center md:text-right">
            <EditableText as="span" scope={{ type: 'global', field: 'footer_tagline' }} value={footerTagline} multiline={false}>{footerTagline}</EditableText>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
